/**
 * @file src/hooks/AuthOrchestrator.js
 */
import { identity } from '../api/identity';
import { storage } from '../utils/storage';


class AuthOrchestrator {
  static async hydrateSession(setUser, setLoading) {
    const hasToken = storage.hasUsableAccessToken();
    if (!hasToken) {
      storage.remove(storage.KEYS.ACCESS_TOKEN);
      storage.remove(storage.KEYS.REFRESH_TOKEN);
      setLoading(false);
      return;
    }

    try {
      const profile = await identity.fetchProfile();
      // One id, two names: the profile carries `id`, the token and every
      // service row say `userId`. Pages compared on either and half of them
      // missed (the org owner lost Remove and Delete Org — walk finding #78),
      // so both are set here, once.
      const uid = String(profile?.userId || profile?.id || profile?._id || '');
      setUser(uid ? { ...profile, id: profile.id || uid, userId: profile.userId || uid } : profile);
      // 🪪 Stamp who this tab is. The API client refuses to send a token whose
      // subject is not this id (utils/identityGuard.js) — the person the page
      // shows and the person the requests act as are the same person.
      const stampedId = profile?.id || profile?._id || null;
      if (stampedId) storage.set(storage.KEYS.USER_ID, String(stampedId));

      // ── Onboarding state reconciliation ────────────────────────────────
      // The server is the source of truth for onboardingState. Sync the
      // localStorage flag from it so users who clear local storage still
      // get directed to /welcome on next login. Also clears stale flags
      // when the server reports the onboarding is already completed.
      const serverOnboardingState = profile?.onboardingState;
      const needsOnboarding = serverOnboardingState === 'just_registered'
        || serverOnboardingState === 'in_progress';

      if (needsOnboarding && !storage.get(storage.KEYS.ONBOARDING_PENDING)) {
        storage.set(storage.KEYS.ONBOARDING_PENDING, {
          email:     profile?.email || '',
          createdAt: new Date().toISOString(),
          source:    'server_reconciliation'
        });
      } else if (serverOnboardingState === 'completed') {
        storage.remove(storage.KEYS.ONBOARDING_PENDING);
      }

      // Do not block initial route hydration on ownership transfer.
      // Transfer can finish in background while the user navigates.
      void identity.claimGuestSession().catch(() => {});
    } catch (err) {
      // Standard 13.1: Graceful purge on session failure
      this.terminateSession(setUser);
    } finally {
      setLoading(false);
    }
  }

  static async terminateSession(setUser) {
    storage.clear();
    // Always evict role selection so a new session starts at the primary role.
    localStorage.removeItem('moksy_active_role');
    setUser(null);
    if (!window.location.pathname.includes('/login')) {
      // Soft navigation: lets React Router handle the redirect without a hard reload.
      window.history.replaceState(null, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }
}

export { AuthOrchestrator };
